import React, { useCallback } from 'react';

import { I18n } from '@aws-amplify/core';
import { Col, Row } from 'antd';
import moment from 'moment';

import { useApplicationContext } from '@/context/v1/Application/context';
import { dateFormat } from '@/utils/date-format';
import KinebotLogo from '@/assets/img/logo.svg';
import { PROFILE_DEFAULT } from '@/utils/enum';
import { InfoRow } from './InfoRow';
import type { KimLhcInformation } from '@/types';

import * as S from '@/components/views/Report/KimLhc/Report/Steps/Result/styles';

interface HeaderProps {
	information: KimLhcInformation;
}

interface InfoItem {
	label: string;
	value: string;
}

export function Header({ information }: Readonly<HeaderProps>) {
	const { organization, company } = useApplicationContext();

	const companyLogo = company?.url_logo !== PROFILE_DEFAULT.URL ? company?.url_logo : undefined;
	const clientLogo = companyLogo ?? organization?.url_logo;
	const dateFormatByLocale = dateFormat();
	const reportDate = moment(information.createdAt).format(dateFormatByLocale);
	const collectionDate = moment(information.collectDate).format(dateFormatByLocale);

	const formatFieldName = useCallback((fieldName: string) => {
		return `${I18n.get(fieldName)}:`;
	}, []);

	const leftColumn: InfoItem[] = [
		{ label: formatFieldName('Company'), value: information.companyName },
		{ label: formatFieldName('Industrial site'), value: information.organizationName },
		{ label: formatFieldName('Sector'), value: information.sectorName },
		{ label: formatFieldName('Cell'), value: information.lineName },
		{ label: formatFieldName('Workstation'), value: information.workstationName },
		{ label: formatFieldName('Evaluator'), value: information.evaluatorName }
	];

	const rightColumn: InfoItem[] = [
		{ label: I18n.get('Collection date:'), value: collectionDate },
		{ label: I18n.get('Report date:'), value: reportDate },
		{
			label: I18n.get('Transporting cargo in pairs:'),
			value: information.transportingCargoInPairs ? I18n.get('Yes') : I18n.get('No')
		},
		{
			label: I18n.get('Work day duration:'),
			value: information.dayDuration ? `${information.dayDuration} ${I18n.get('minutes')}` : '-'
		},
		{
			label: I18n.get('Activity duration:'),
			value: information.activityDuration ? `${information.activityDuration} ${I18n.get('minutes')}` : '-'
		}
	];

	const bottomRow: InfoItem[] = [
		{ label: formatFieldName('Activity'), value: information.activityName },
		{ label: formatFieldName('Video file'), value: information.fileName }
	];

	return (
		<S.HeaderContainer>
			<Row gutter={[0, 24]}>
				<Col span={24}>
					<S.HeaderTop>
						<S.LogoKinebot src={KinebotLogo} alt="Kinebot" />
						<S.TitleContainer>
							<S.SubTitle>{I18n.get('Results')}</S.SubTitle>
							<S.MainTitle>{I18n.get('KIM LHC: Manual Lifting, Holding and Carrying')}</S.MainTitle>
						</S.TitleContainer>
						{clientLogo && <S.ClientLogo src={clientLogo} alt="Client Logo" />}
					</S.HeaderTop>
				</Col>
				<Col span={24}>
					<S.InfoSection>
						<S.InfoSectionTitle>{I18n.get('General information')}</S.InfoSectionTitle>
						<S.InfoGrid>
							<S.InfoColumn>
								{leftColumn.map((item, index) => (
									<InfoRow key={index} label={item.label} value={item.value} />
								))}
							</S.InfoColumn>
							<S.VerticalDivider />
							<S.InfoColumn>
								{rightColumn.map((item, index) => (
									<InfoRow key={index} label={item.label} value={item.value} />
								))}
							</S.InfoColumn>
							<S.InfoLastColumn>
								{bottomRow.map((item, index) => (
									<InfoRow key={index} label={item.label} value={item.value} />
								))}
							</S.InfoLastColumn>
						</S.InfoGrid>
					</S.InfoSection>
				</Col>
			</Row>
		</S.HeaderContainer>
	);
}
